import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, ChevronRight, FolderClosed, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtIDR } from "@/lib/constants";
import { Input } from "@/components/ui/input";

export default function ArchiveList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [folders, setFolders] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/batches/archive");
      setFolders(data);
    } catch (e) {
      toast.error(apiError(e));
      setFolders([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = search.trim().toLowerCase();
  const rows = (folders || []).filter(
    (b) =>
      !q ||
      (b.name || "").toLowerCase().includes(q) ||
      (b.closed_at ? format(new Date(b.closed_at), "d MMM yyyy").toLowerCase().includes(q) : false)
  );

  return (
    <div data-testid="po-archive-page">
      <button
        data-testid="archive-list-back"
        onClick={() => navigate("/")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> PO Batches
      </button>
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">PO Archive</h1>
      <p className="text-sm text-muted-foreground">Closed batches — kept forever, one folder per batch</p>

      <div className="relative my-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input
          data-testid="archive-search-input"
          className="h-12 pl-9"
          placeholder="Search batch name or date…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {folders === null ? (
        <div className="grid place-items-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="po-archive-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <div>
            <FolderClosed className="mx-auto mb-3 h-10 w-10 text-stone-300" />
            <p className="font-medium text-muted-foreground">No archived batches yet</p>
            <p className="text-sm text-muted-foreground">Closing a batch moves it here — history is kept forever.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="po-archive-list">
          {rows.map((b) => (
            <div
              key={b.id}
              data-testid={`archive-folder-${b.id}`}
              onClick={() => navigate(`/archive/${b.id}`)}
              className="w-full cursor-pointer rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate font-heading text-lg font-bold tracking-tight">
                    <FolderClosed className="h-4 w-4 shrink-0 text-stone-400" /> {b.name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {b.closed_at ? `Closed ${format(new Date(b.closed_at), "d MMM yyyy")}` : "Original batch record deleted"}
                    {" · "}
                    {b.total_orders} order(s) · {b.total_invoices} invoice(s)
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-stone-300" />
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {b.recovered && (
                  <span data-testid={`archive-recovered-${b.id}`} className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                    RECOVERED
                  </span>
                )}
                {["owner", "admin_input", "admin"].includes(user.role) && (
                  <>
                    <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      Received {fmtIDR(b.paid_total)}
                    </span>
                    <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                      Outstanding {fmtIDR(b.outstanding_total)}
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
